import React, { useEffect } from 'react'
import { Pressable, View } from 'react-native'
import Animated, { useAnimatedStyle } from 'react-native-reanimated'

import Button from '@ui/buttons/Button'
import InputContainer from '@ui/forms/Input/InputContainer'
import InputTextEnd from '@ui/forms/Input/InputTextEnd'
import useAnimatedToggle from '@ui/hooks/useAnimatedToggle'
import { useTranslation } from 'react-i18next'
import cn from '@ui/utils/cn'
import { SelectButtonProps, SelectSuffixProps } from './SelectProps'
import { ToggleIcon } from './ToggleIcon'
import { useSharedValue, withSpring } from 'react-native-reanimated'
import InputLabelTop from '@ui/forms/Input/InputLabelTop'

export const SelectSuffix = ({
  textEnd,
  isLoading,
  onClear,
  hiddenChevron,
  disabled,
  active,
  error,
}: SelectSuffixProps) => {
  const disabledAnim = useAnimatedToggle(disabled || error || false)

  return (
    <>
      {textEnd && (
        <InputTextEnd
          text={textEnd}
          className={cn(!hiddenChevron && 'pointer-events-none pr-0')}
        />
      )}
      {!!onClear && (
        <Button
          variant="outlined"
          size="xs"
          iconStart="x"
          icon="only"
          className="border-0"
          onPress={onClear}
        />
      )}
      {!hiddenChevron && (
        <View className="pointer-events-none">
          <ToggleIcon
            loading={isLoading}
            animation={active}
            hidden={disabledAnim}
          />
        </View>
      )}
    </>
  )
}

export const SelectButton = ({
  label,
  labelVariant = 'inline',
  placeholder,
  icon,
  required,
  style,
  className,
  active,
  onPress,
  value,
  successMessage,
  errorMessage,
  isSuccess,
  classNameTextInput,
  center,
  // textEnd,
  // isLoading,
  hideIndicator,
  isEmpty,
  suffixWidth,
  textOpacity,
  ...rest
}: SelectButtonProps) => {
  const { t } = useTranslation(['ui'])
  const success = useSharedValue(0)
  const error = useSharedValue(0)
  const disabled = useSharedValue(0)
  const readonly = useSharedValue(0)

  const textOpacityStyle = useAnimatedStyle(() => ({
    opacity: textOpacity ? 1 - active.value : 1,
  }))

  const text = isEmpty
    ? t('ui:selectEmpty')
    : value == null
    ? placeholder
    : value

  useEffect(() => {
    success.set(withSpring(successMessage || isSuccess ? 1 : 0))
    error.set(withSpring(errorMessage ? 1 : 0))
    disabled.set(withSpring(rest.disabled ? 1 : 0))
    readonly.set(withSpring(rest.readonly ? 1 : 0))
  }, [
    success,
    successMessage,
    isSuccess,
    error,
    errorMessage,
    disabled,
    readonly,
    rest.disabled,
    rest.readonly,
  ])

  const inputContainer = (
    <InputContainer
      success={success}
      error={error}
      style={style}
      className={className}
      label={label}
      labelVariant={labelVariant}
      icon={icon}
      required={required}
      focus={active}
      onLabelPress={onPress}
      readonly={readonly}
      errorMessage={errorMessage}
      successMessage={successMessage}
      center={center}
      disabled={disabled}
      hideIndicator={hideIndicator}
    >
      <Pressable
        onPress={onPress}
        tabIndex={onPress ? 0 : -1}
        className={cn(
          'flex-1 flex-row items-center justify-between',
          rest.disabled && 'pointer-events-none'
        )}
      >
        <Animated.Text
          // RN has some issues updating this
          key={value}
          style={textOpacityStyle}
          className={cn(
            'px-6 font-text-semibold',
            value === null && 'color-text/25',
            !!value && 'color-black',
            !value && 'color-black/25',
            rest.disabled && !isEmpty && 'color-black/25',
            isEmpty && 'color-black/25',
            classNameTextInput
          )}
        >
          {!!text && text}
        </Animated.Text>
        {/* Reserve space for the suffix elements */}
        {!!suffixWidth && (
          <View
            className="pointer-events-none h-full"
            style={{
              width: suffixWidth,
            }}
          />
        )}
      </Pressable>
    </InputContainer>
  )

  if (labelVariant !== 'top') {
    return inputContainer
  }

  return (
    <View className="flex flex-col gap-3">
      <InputLabelTop
        label={label}
        required={required}
        success={success}
        error={error}
        disabled={disabled}
        readonly={readonly}
        focus={active}
      />
      {inputContainer}
    </View>
  )
}
